import React, { useRef, useState, useEffect, MouseEvent } from 'react';
import { debounce } from 'throttle-debounce';
import { addEvent, removeEvent } from 'ava-util/dist/dom-event';
import cn from 'classnames';
import styles from './index.module.less';
import { useTrans } from 'base/utils/i18n';
import { hasSubmenu, moduleId, track } from 'mod/store/nav/utils';
import { NavLink } from 'mod/store/nav/NavLink';
import { useStoreState } from 'base/store';
import { LoopRender } from '@base/snippets/LoopRender';
import { NavClassicList } from './NavClassicList';
import Iconfont from '@base/lib/Iconfont';

type IProps = {
    className?: string;
    popupClassName?: string;
    onClose?: () => void;
}

export const Nav: React.FC<IProps> = ({ className, onClose }) => {
    const { ua: { isMobile } } = useStoreState('rootStore');
    const { navList: navListStore } = useStoreState('persistStore') || {};
    const [visible, setVisible] = useState<boolean>(false);
    const [curIndex, setCurIndex] = useState<number>(-1);//保存当前submenu对应的一级分类的索引
    const navRef = useRef<HTMLDivElement>(null) //最外层容器
    const indexRef = useRef<number>(-1);
    const { t } = useTrans();

    const navList = navListStore?.list || []

    const showSubmenu = () => {
        if (indexRef.current > -1) {
            setCurIndex(indexRef.current);
            setVisible(true);
        }
    };

    const hideSubmenu = () => {
        indexRef.current = -1;
        setCurIndex(-1);
        setVisible(false);
    };

    const debounceShowSubmenu = debounce(200, showSubmenu);

    const handleMouseEnter = (index: number) => {
        if (hasSubmenu(navList[index])) {
            indexRef.current = index;
            if (visible) {
                showSubmenu()
            } else {
                debounceShowSubmenu();
            }
        } else {
            hideSubmenu();
        }
    };

    const handleMobileClick = (e: React.MouseEvent, index: number) => {
        const item = navList[index];

        if (!hasSubmenu(item)) {
            hideSubmenu();
            return;
        }

        if (!visible) {
            setVisible(true);
        }
        if (curIndex !== index || !visible) {
            e.preventDefault();
        }
        if (curIndex === index) {
            setVisible(false);
            setCurIndex(-1);
        } else {
            setCurIndex(index);
        }
    };

    useEffect(() => {
        track({
            action: 'impr',
            target: 'menu',
            relTarget: 'menu',
        });

        addEvent(window, 'resize', onClose);
        addEvent(window, 'scroll', onClose);
        return () => {
            removeEvent(window, 'resize', onClose);
            removeEvent(window, 'scroll', onClose);
        };
    }, []);

    return (
        <div
            className={cn(className, styles.nav, 'flex')}
            ref={navRef}
            data-t-mod={moduleId}
        >
            <div className={'g-level-col'}>
                <h3 className={'g-title flex flex-middle'}>
                    <Iconfont className={'icon-menu'}/><span>{t('web_common_categories')}</span>
                </h3>

                <LoopRender
                    tag={false}
                    list={navList}
                    render={(item, { index }) => (
                        <NavLink
                            key={index}
                            item={item}
                            relTarget={'menu'}
                            position={index + 1}
                            className={cn('g-link', { 'g-active': curIndex === index })}
                            // 移动端需要在子菜单展示后再跳转
                            {...(isMobile ? {
                                onClick: e => handleMobileClick(e as MouseEvent, index)
                            } : {
                                onMouseEnter: () => handleMouseEnter(index),
                                onClick: onClose
                            })}
                        >
                            {item.title}{item.classicContent.list.length > 0 ? ' >' : ''}
                        </NavLink>
                    )}
                />
            </div>

            {navList[curIndex] && (
                <NavClassicList
                    lv1Item={navList[curIndex]}
                    lv1Index={curIndex}
                    list={navList[curIndex].classicContent.list}
                    onClose={onClose}
                />
            )}
        </div>
    )
}